import axios from "axios";
import { useSnackbar } from "notistack";
import { getToken, logout } from "src/common/httpRequests";
import useLocales from "./useLocales";

const useHttpRequest = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { translate } = useLocales("dynamicMessages");

  async function GetAsync(
    baseUrl: string,
    shouldUseToken = true,
    cancelToken?: any,
    options?: any,
  ) {
    let headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };
    if (shouldUseToken) headers["Authorization"] = await getToken();

    return await axios
      .get(baseUrl, { headers, cancelToken: cancelToken, ...options })
      .then(
        (res) => {
          return res;
        },
        (error) => {
          console.log(error.message, error);
          if (error?.response && error?.response?.status === 401) {
            logout();
          }
          if (error.message == "Network Error") {
            throw new Error("Network Error");
          }
          return error.response;
        },
      );
  }

  async function PostAsync(
    baseUrl: string,
    body?: any,
    shouldUseToken = true,
    options = {
      valueFormatter: undefined as
        | ((value: string | number) => string)
        | undefined,
    },
    translateSpecificPage?: (text: any, options?: any) => string,
  ) {
    const t = translateSpecificPage ?? translate;
    let headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    if (shouldUseToken) headers["Authorization"] = await getToken();

    return await axios.post(baseUrl, body, { headers }).then(
      (res) => res,
      (error) => {
        if (error.response.status === 401) {
          logout();
        } else if (error.response.status === 403) {
          error.response.data = {
            Title: "Forbidden Request",
            Status: error.response.status,
            Detail: "permission_not_granted",
          };
        } else if (error.response.status === 404) {
          enqueueSnackbar(t("resource_not_found"), {
            variant: "error",
          });
        } else if (error.response.status === 400) {
          if (
            error.response.data?.DynamicMessages &&
            error.response.data?.DynamicMessages.length > 0
          ) {
            let message = "";
            error.response.data.DynamicMessages.forEach((element) => {
              if (element?.Values) {
                const value = element?.Values?.reduce(
                  (obj, value: string | number, index: number) => {
                    obj[index] = options?.valueFormatter
                      ? options?.valueFormatter(value)
                      : value;
                    return obj;
                  },
                  {},
                );
                const result = t(element?.MessageKey, value);
                message += result + "\n";
              } else {
                message += t(element?.MessageKey) + "\n";
              }
            });
            enqueueSnackbar(message, {
              variant: "error",
              autoHideDuration: 7000,
            });
          } else {
            enqueueSnackbar(t(error.response.data.Detail), {
              variant: "error",
            });
          }
        } else {
          const errMsg = error.response.data.Detail;
          if (errMsg.includes("was not recognized as a valid DateTime.")) {
            const [type, date, message] = errMsg.split(" ");
            const translatedMsg = t("invalid_dateTime", { date });
            enqueueSnackbar(translatedMsg, {
              variant: "error",
            });
          } else {
            enqueueSnackbar(t(error.response.data.Detail), {
              variant: "error",
            });
          }
        }
        return error.response;
      },
    );
  }

  async function PutAsync(
    baseUrl: string,
    body: any,
    shouldUseToken = true,
    options = {
      valueFormatter: undefined as
        | ((value: string | number) => string)
        | undefined,
    },
  ) {
    let headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };
    if (shouldUseToken) headers["Authorization"] = await getToken();

    return await axios.put(baseUrl, body, { headers }).then(
      (res) => {
        return res;
      },
      (error) => {
        if (error.response.status === 401) {
          logout();
        } else if (error.response.status === 403) {
          error.response.data = {
            Title: "Forbidden Request",
            Status: error.response.status,
            Detail: "permission_not_granted",
          };
        } else if ([400, 404].includes(error.response.status)) {
          if (
            error.response.data?.DynamicMessages &&
            error.response.data?.DynamicMessages.length > 0
          ) {
            let message = "";
            error.response.data.DynamicMessages.forEach((element) => {
              if (element?.Values) {
                const value = element?.Values?.reduce(
                  (obj, value: string | number, index: number) => {
                    obj[index] = options?.valueFormatter
                      ? options?.valueFormatter(value)
                      : value;
                    return obj;
                  },
                  {},
                );
                const result = translate(element?.MessageKey, value);
                message += result + "\n";
              } else {
                message += translate(element?.MessageKey) + "\n";
              }
            });
            enqueueSnackbar(message, {
              variant: "error",
              autoHideDuration: 7000,
            });
          }
        }
        return error.response;
      },
    );
  }

  async function PatchAsync(
    baseUrl: string,
    body?: any,
    shouldUseToken = true,
    options = {
      valueFormatter: undefined as
        | ((value: string | number) => string)
        | undefined,
    },
  ) {
    let headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };
    if (shouldUseToken) headers["Authorization"] = await getToken();

    return await axios.patch(baseUrl, body, { headers }).then(
      (res) => {
        return res;
      },
      (error) => {
        if (error.response.status === 401) {
          logout();
        } else if (error.response.status === 403) {
          error.response.data = {
            Title: "Forbidden Request",
            Status: error.response.status,
            Detail: "permission_not_granted",
          };
        } else if ([400, 404].includes(error.response.status)) {
          if (
            error.response.data?.DynamicMessages &&
            error.response.data?.DynamicMessages.length > 0
          ) {
            let message = "";
            error.response.data.DynamicMessages.forEach((element) => {
              if (element?.Values) {
                const value = element?.Values?.reduce(
                  (obj, value: string | number, index: number) => {
                    obj[index] = options?.valueFormatter
                      ? options?.valueFormatter(value)
                      : value;
                    return obj;
                  },
                  {},
                );
                const result = translate(element?.MessageKey, value);
                message += result + "\n";
              } else {
                message += translate(element?.MessageKey) + "\n";
              }
            });
            enqueueSnackbar(message, {
              variant: "error",
              autoHideDuration: 7000,
            });
          }
        }
        return error.response;
      },
    );
  }

  return { GetAsync, PostAsync, PutAsync, PatchAsync };
};
export default useHttpRequest;
